/*
 * @author        david.loehr@zumper.com
 * @copyright     Copyright (c) 2024, Zumper
 */

import { REVIVE_CONTENTS_FIRST, typeRegistry } from './reviveBizos'
import {
  ENTRIES,
  type RawSerializableMap,
  type SerializableMap as SerializableMapType,
  __typename,
} from './serializableMap.types'

class _SerializableMap<Key, Value> extends Map<Key, Value> {
  static isRawSerializableMap<Key, Value>(
    iterableOrEntries: unknown
  ): iterableOrEntries is RawSerializableMap<unknown, unknown> {
    return (
      typeof iterableOrEntries === 'object' &&
      iterableOrEntries != null &&
      !(Symbol.iterator in iterableOrEntries) &&
      '__typename' in iterableOrEntries &&
      iterableOrEntries.__typename === __typename
    )
  }
  static isRawSerializableMapOfType<Key, Value>(
    iterableOrEntries: unknown,
    keyTypeGuard: (key: unknown) => key is Key,
    valueTypeGuard: (value: unknown) => value is Value
  ): iterableOrEntries is RawSerializableMap<Key, Value> {
    return (
      _SerializableMap.isRawSerializableMap(iterableOrEntries) &&
      iterableOrEntries.e.every(([key, value]) => keyTypeGuard(key) && valueTypeGuard(value))
    )
  }
  static readonly [REVIVE_CONTENTS_FIRST] = true
  constructor(
    iterableOrEntries?:
      | RawSerializableMap<Key, Value>
      | Iterable<readonly [Key, Value]>
      | readonly (readonly [Key, Value])[]
      | null
  ) {
    super(
      _SerializableMap.isRawSerializableMap(iterableOrEntries)
        ? iterableOrEntries[ENTRIES]
        : iterableOrEntries
    )
  }
  toJSON() {
    return {
      __typename,
      [ENTRIES]: Array.from(this.entries()),
    } as const
  }
  filter<E extends readonly [key: Key, value: Value]>(
    predicate: (entry: readonly [key: Key, value: Value]) => entry is E
  ): SerializableMap<E[0], E[1]>
  filter(
    predicate: (entry: readonly [key: Key, value: Value]) => unknown
  ): SerializableMap<Key, Value>
  filter(
    predicate: (entry: readonly [key: Key, value: Value]) => unknown
  ): SerializableMap<Key, Value> {
    const filteredEntries = Array.from(this.entries()).filter(predicate)
    if (filteredEntries.length === this.size) {
      return this
    }
    return new SerializableMap(filteredEntries)
  }
  with(key: Key, value: Value) {
    return new SerializableMap([...this.entries(), [key, value]])
  }
}

export const SerializableMap: typeof SerializableMapType = _SerializableMap
export type SerializableMap<Key, Value> = SerializableMapType<Key, Value>

typeRegistry.add(__typename, SerializableMap)
